import { clearEntryIntent } from "./entry-intent";
import { track } from "./telemetry";
import { create } from "zustand";
import { config, storage } from "./config";
import { getApi } from "../adapters";
import { subscribe } from "../adapters/realtime";
import type {
  Context,
  Credential,
  Profile,
  Ticket,
  Session,
  Emission,
} from "../domain/types";
import { ApiError } from "../adapters/http";
import {
  expired,
  feedbackAnswered,
  mapTicket,
  shouldRestore,
} from "../domain/rules";
interface State {
  ready: boolean;
  credential: Credential | null;
  profile: Profile | null;
  context: Context | null;
  ticket: Ticket | null;
  feedbackTicket: Ticket | null;
  busy: boolean;
  error: string | null;
  connection: string;
  notice: string | null;
  init(): Promise<void>;
  loadContext(key: string): Promise<void>;
  resumeTicket(): Promise<void>;
  refresh(initial?: boolean): Promise<void>;
  emit(session: Session): Promise<void>;
  startEntry(key: string): Promise<void>;
  action(name: string, reason?: string): Promise<void>;
  checkin(code: string): Promise<void>;
  clear(feedbackId?: number): void;
  saveProfile(profile: Profile): Promise<void>;
  acceptLogin(credential: Credential, profile: Profile): void;
  logout(): Promise<void>;
}
let contextRevision = 0,
  ticketRevision = 0,
  sessionRevision = 0;
let startPromise: Promise<void> | null = null;
let refreshPromise: Promise<void> | null = null;
export const useStore = create<State>((set, get) => ({
  ready: false,
  credential: null,
  profile: null,
  context: null,
  ticket: null,
  feedbackTicket: null,
  busy: false,
  error: null,
  connection: "disconnected",
  notice: null,
  async init() {
    if (config.mock) {
      const { seedMock } = await import("../adapters/mock");
      seedMock();
    }
    set({
      credential: storage.get("credential"),
      profile: storage.get("profile"),
      context: storage.get("context"),
      feedbackTicket: storage.get("feedbackTicket"),
    });
    try {
      if (get().credential) await get().refresh(true);
    } catch (e) {
      set({ error: errorCode(e) });
    } finally {
      set({ ready: true });
    }
  },
  async resumeTicket() {
    const ticket = get().ticket;
    if (!ticket) throw new ApiError("expired");
    const revision = sessionRevision;
    storage.set("ticketId", ticket.id);
    await get().refresh();
    const restored = get().ticket;
    if (revision !== sessionRevision || !restored || restored.id !== ticket.id)
      throw new ApiError("expired");
    // An existing ticket supplies its store details. A terminal available for
    // new admissions is not required to follow an existing attendance.
    if (get().context?.key !== restored.accessKey) {
      contextRevision++;
      storage.remove("context");
      set({ context: null });
    }
    clearEntryIntent();
  },
  async loadContext(key) {
    if (!/^[A-Za-z0-9_-]{1,100}$/.test(key)) throw new ApiError("invalidCode");
    const revision = ++contextRevision;
    track("checkout.access_key_validated");
    set({ context: null });
    storage.remove("context");
    const api = await getApi();
    const terminal = await api.terminal(key);
    if (
      !terminal.provider?.id ||
      !terminal.location?.id ||
      !terminal.services?.[0]?.id
    )
      throw new ApiError("storeMissing");
    if (revision !== contextRevision) return;
    const context = {
      key,
      terminal,
      serviceId: terminal.services[0].id,
      color: terminal.headerBackgroundColor || "#7620d9",
      logo: terminal.providerLogoUrl || "/assets/images/fz-icon-default.svg",
    };
    storage.set("context", context);
    set({ context });
    void api
      .template(terminal.provider.slug)
      .then((template) => {
        if (revision !== contextRevision) return;
        const branded = {
          ...context,
          color: template.templateColor || context.color,
          logo: template.avatarUrl || context.logo,
        };
        storage.set("context", branded);
        set({ context: branded });
      })
      .catch(() => {});
  },
  async refresh(initial = false) {
    if (refreshPromise) return refreshPromise;
    const id = storage.get<number>("ticketId");
    if (!id) {
      set({ ticket: null });
      return;
    }
    const revision = ticketRevision;
    const authRevision = sessionRevision;
    refreshPromise = (async () => {
      const api = await getApi();
      const t = await api.ticket(id);
      if (revision !== ticketRevision || authRevision !== sessionRevision)
        return;
      if (
        (initial && !shouldRestore(t, storage.get<number>("lastFeedback"))) ||
        expired(t) ||
        t.status === "canceled" ||
        feedbackAnswered(t)
      ) {
        if (feedbackAnswered(t)) {
          storage.set("feedbackTicket", t);
          set({ feedbackTicket: t });
        }
        storage.remove("ticketId");
        set({ ticket: null, notice: expired(t) ? "expired" : null });
        return;
      }
      if (t.status !== "authorized") {
        try {
          t.position = (await api.position(t)) || "--";
        } catch {
          /* Ticket content remains useful without a position estimate. */
        }
      }
      if (revision !== ticketRevision || authRevision !== sessionRevision)
        return;
      set({
        ticket: mapTicket(t as unknown as Record<string, unknown>),
        error: null,
      });
      if (t.status === "completed") {
        storage.set("feedbackTicket", t);
        set({ feedbackTicket: t });
      }
    })().finally(() => {
      refreshPromise = null;
    });
    return refreshPromise;
  },
  async startEntry(key) {
    if (startPromise) return startPromise;
    const revision = sessionRevision;
    const context = get().context;
    if (!context || context.key !== key || !get().credential || !get().profile)
      throw new ApiError("storeMissing");
    startPromise = (async () => {
      const pending = storage.get<Emission>("pendingEmission");
      if (pending?.ticketsIds[0])
        storage.set("ticketId", pending.ticketsIds[0]);
      if (storage.get("ticketId")) {
        await get().refresh();
        if (revision !== sessionRevision || get().context?.key !== key)
          throw new ApiError("sessionExpired");
        if (pending) storage.remove("pendingEmission");
        if (get().ticket) {
          if (get().ticket?.accessKey && get().ticket?.accessKey !== key)
            throw new ApiError("ticketExists");
          return;
        }
      }
      const sessions = await (await getApi()).sessions(context);
      if (revision !== sessionRevision || get().context?.key !== key)
        throw new ApiError("sessionExpired");
      if (!sessions.sessions.length) throw new ApiError("noSessions");
      const available = sessions.sessions.find((s) => s.hasSlotsLeft);
      if (!available) throw new ApiError("noSlots");
      await get().emit(available);
      if (!get().ticket) throw new ApiError("ticketMissing");
    })().finally(() => {
      startPromise = null;
    });
    return startPromise;
  },
  async emit(session) {
    if (get().busy) return;
    const { context, profile } = get();
    if (!context || !profile || !session.hasSlotsLeft)
      throw new ApiError("storeMissing");
    const authRevision = sessionRevision;
    set({ busy: true, error: null });
    let stop: undefined | (() => void);
    try {
      const api = await getApi();
      let emission = storage.get<Emission>("pendingEmission");
      if (storage.get<number>("ticketId") && !emission) {
        await get().refresh();
        return;
      }
      if (!emission) {
        emission = await api.emit({
          pId: context.terminal.provider.id,
          locationId: context.terminal.location.id,
          customer: {
            name: [profile.firstName, profile.lastName]
              .filter(Boolean)
              .join(" "),
            email: profile.email,
            phone: profile.phone,
          },
          tickets: [
            {
              serviceId: context.serviceId,
              resourceId: session.resourceId,
              sessionId: session.id,
              bookedStartTime: session.start,
              attendanceType: 2,
              priority: 0,
            },
          ],
        });
        if (authRevision !== sessionRevision) return;
        storage.set("pendingEmission", emission);
        await new Promise<void>((resolve, reject) => {
          let settled = false;
          const timer = setTimeout(() => {
            settled = true;
            resolve();
          }, 15000);
          const done = (event: unknown) => {
            if (settled) return;
            settled = true;
            clearTimeout(timer);
            if ((event as { status?: string }).status === "unauthorized") {
              storage.remove("pendingEmission");
              reject(new ApiError("scheduleDenied"));
            } else resolve();
          };
          subscribe(`schedule-${emission!.aggregatorId}`, {
            ScheduleAuthorized: done,
            ScheduleUnauthorized: () => done({ status: "unauthorized" }),
          })
            .then((fn) => {
              stop = fn;
              if (settled) fn();
            })
            .catch(() => {
              clearTimeout(timer);
              resolve();
            });
        });
      }
      if (authRevision !== sessionRevision) return;
      const id = emission.ticketsIds[0];
      if (!id) throw new ApiError("generic");
      storage.set("ticketId", id);
      await get().refresh();
      storage.remove("pendingEmission");
      track("checkout.scheduled");
    } catch (e) {
      set({ error: errorCode(e) });
      throw e;
    } finally {
      stop?.();
      set({ busy: false });
    }
  },
  async action(name, reason = "") {
    if (get().busy) return;
    const ticket = get().ticket;
    if (!ticket) throw new ApiError("ticketMissing");
    set({ busy: true });
    try {
      await (await getApi()).action(ticket, name, reason || null);
      await get().refresh();
    } finally {
      set({ busy: false });
    }
  },
  async checkin(code) {
    const revision = sessionRevision;
    if (get().busy) return;
    const ticket = get().ticket;
    if (!ticket) throw new ApiError("ticketMissing");
    if (!/^\d{6}$/.test(code)) throw new ApiError("invalidCode");
    set({ busy: true });
    try {
      const partial = await (await getApi()).checkin(ticket, code);
      if (revision !== sessionRevision) return;
      if (partial) set({ ticket: partial });
      track("checkout.ticket_validated");
      try {
        await get().refresh();
      } catch (e) {
        if (!partial) throw e;
        set({ notice: "syncPending" });
      }
    } finally {
      set({ busy: false });
    }
  },
  clear(feedbackId) {
    ticketRevision++;
    storage.remove("ticketId");
    storage.remove("pendingEmission");
    storage.remove("feedbackTicket");
    if (feedbackId) storage.set("lastFeedback", feedbackId);
    set({ ticket: null, feedbackTicket: null, error: null });
  },
  async saveProfile(profile) {
    const revision = sessionRevision;
    await (await getApi()).saveProfile(profile);
    if (revision !== sessionRevision) return;
    storage.set("profile", profile);
    set({ profile });
  },
  acceptLogin(credential, profile) {
    sessionRevision++;
    storage.set("credential", credential);
    storage.set("profile", profile);
    set({ credential, profile });
  },
  async logout() {
    clearEntryIntent();
    sessionRevision++;
    contextRevision++;
    const profile = get().profile;
    try {
      if (profile?.device?.registrationId)
        await (await getApi()).device(profile.device.registrationId, true);
    } catch {
      /* Local logout must always complete. */
    }
    storage.remove("credential");
    storage.remove("profile");
    storage.remove("verification");
    for (const stage of ["basic", "additional", "edit"])
      storage.remove(`profileDraft:${stage}`);
    get().clear();
    set({ credential: null, profile: null });
    const { signOut } = await import("../adapters/auth");
    await signOut();
    if ("caches" in window) {
      const keys = await caches.keys();
      await Promise.all(
        keys.filter((k) => k.startsWith("fz-api")).map((k) => caches.delete(k)),
      );
    }
  },
}));
export function errorCode(error: unknown) {
  return error instanceof Error ? error.message : "generic";
}
export function sessionExpired() {
  clearEntryIntent();
  sessionRevision++;
  storage.remove("credential");
  storage.remove("profile");
  useStore.getState().clear();
  useStore.setState({
    credential: null,
    profile: null,
    notice: "sessionExpired",
  });
}
